import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SpeakerIcon, EyeOffIcon, SparklesIcon } from 'lucide-react';
const philosophies = [{
  id: 'reference',
  name: 'Reference',
  icon: SpeakerIcon,
  color: 'text-cinema',
  bgColor: 'bg-cinema',
  borderColor: 'border-cinema',
  desc: 'Studio-grade monitors. For those who want to see and hear perfection.',
  stats: {
    clarity: 98,
    immersion: 90,
    invisibility: 20
  },
  waveConfig: {
    count: 6,
    spacing: 10,
    duration: 1.5,
    blur: 'blur-sm'
  }
}, {
  id: 'invisible',
  name: 'Invisible',
  icon: EyeOffIcon,
  color: 'text-relax',
  bgColor: 'bg-relax',
  borderColor: 'border-relax',
  desc: 'Architecture-first. Sound fills the room from nowhere.',
  stats: {
    clarity: 85,
    immersion: 95,
    invisibility: 99
  },
  waveConfig: {
    count: 3,
    spacing: 30,
    duration: 3,
    blur: 'blur-xl'
  }
}, {
  id: 'lifestyle',
  name: 'Lifestyle',
  icon: SparklesIcon,
  color: 'text-music',
  bgColor: 'bg-music',
  borderColor: 'border-music',
  desc: 'Objects of desire. Speakers as sculptural art.',
  stats: {
    clarity: 90,
    immersion: 88,
    invisibility: 60
  },
  waveConfig: {
    count: 4,
    spacing: 20,
    duration: 2,
    blur: 'blur-md'
  }
}];
export function SoundDemo() {
  const [activeId, setActiveId] = useState('invisible');
  const activePhil = philosophies.find(p => p.id === activeId)!;
  return <section className="py-32 bg-void relative border-t border-graphite" data-id="element-450">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-451">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-452">
          <h2 className="text-headline font-serif text-platinum mb-4" data-id="element-453">
            Experience the Difference
          </h2>
          <p className="text-silver max-w-2xl mx-auto" data-id="element-454">
            Select a speaker philosophy. Feel the difference in how sound shapes
            your space.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12" data-id="element-455">
          {philosophies.map(phil => {
          const isSelected = activeId === phil.id;
          const Icon = phil.icon;
          return <button key={phil.id} onClick={() => setActiveId(phil.id)} className={`p-6 rounded-2xl border text-left transition-all duration-300 ${isSelected ? `bg-carbon ${phil.borderColor} shadow-[0_0_20px_rgba(0,0,0,0.5)]` : 'bg-obsidian border-graphite hover:border-silver/30'}`} data-id="element-456">
                <div className="flex items-center mb-4" data-id="element-457">
                  <div className={`p-3 rounded-full ${isSelected ? `${phil.bgColor}/20` : 'bg-carbon'} mr-4`} data-id="element-458">
                    <Icon className={`w-6 h-6 ${isSelected ? phil.color : 'text-silver'}`} data-id="element-459" />
                  </div>
                  <h3 className={`font-serif text-xl ${isSelected ? 'text-platinum' : 'text-silver'}`} data-id="element-460">
                    {phil.name}
                  </h3>
                </div>
                <p className="text-sm text-silver" data-id="element-461">{phil.desc}</p>
              </button>;
        })}
        </div>

        <div className="bg-carbon border border-graphite rounded-3xl p-8 md:p-12 relative overflow-hidden flex flex-col items-center justify-center min-h-[400px]" data-id="element-462">
          {/* Sound Visualization */}
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden pointer-events-none" data-id="element-463">
            <AnimatePresence mode="wait" data-id="element-464">
              <motion.div key={activeId} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.5
            }} className="relative flex items-center justify-center w-full h-full" data-id="element-465">
                {Array.from({
                length: activePhil.waveConfig.count
              }).map((_, i) => <motion.div key={i} className={`absolute rounded-full border ${activePhil.borderColor} ${activePhil.waveConfig.blur} opacity-20`} initial={{
                width: 0,
                height: 0,
                opacity: 0.5
              }} animate={{
                width: `${100 + i * activePhil.waveConfig.spacing}%`,
                height: `${100 + i * activePhil.waveConfig.spacing}%`,
                opacity: 0
              }} transition={{
                duration: activePhil.waveConfig.duration,
                repeat: Infinity,
                delay: i * (activePhil.waveConfig.duration / activePhil.waveConfig.count),
                ease: 'linear'
              }} style={{
                aspectRatio: '1/1'
              }} data-id="element-466" />)}
                <div className={`w-4 h-4 rounded-full ${activePhil.bgColor} shadow-[0_0_20px_currentColor] ${activePhil.color} z-10`} data-id="element-467" />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Stats Overlay */}
          <div className="relative z-10 w-full max-w-2xl mt-auto pt-48" data-id="element-468">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 bg-obsidian/80 backdrop-blur-md p-6 rounded-2xl border border-graphite" data-id="element-469">
              {[{
              label: 'Clarity',
              value: activePhil.stats.clarity
            }, {
              label: 'Immersion',
              value: activePhil.stats.immersion
            }, {
              label: 'Invisibility',
              value: activePhil.stats.invisibility
            }].map(stat => <div key={stat.label} data-id="element-470">
                  <div className="flex justify-between text-xs uppercase tracking-widest text-silver mb-2" data-id="element-471">
                    <span data-id="element-472">{stat.label}</span>
                    <span className="text-platinum" data-id="element-473">{stat.value}%</span>
                  </div>
                  <div className="h-1.5 w-full bg-carbon rounded-full overflow-hidden" data-id="element-474">
                    <motion.div className={`h-full ${activePhil.bgColor}`} initial={{
                  width: 0
                }} animate={{
                  width: `${stat.value}%`
                }} transition={{
                  duration: 0.8,
                  ease: 'easeOut'
                }} data-id="element-475" />
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}